import type {
  Scalars,
  UpdateCustomDatasourceMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

export interface UpdateCustomDatasourceTRPCInput {
  Id: string;
  Title: string;
  Datasources: Scalars['jsonb']['input'];
  Fields: Scalars['jsonb']['input'];
  Filters: Scalars['jsonb']['input'];
}

/**
 * Maps the tRPC mutation response to the GraphQL mutation shape so consumers
 * don't branch on which path ran.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateCustomDatasourceMutation => {
  return {
    update_custom_datasource_by_pk: {
      __typename: 'custom_datasource',
      Id: trpcData.Id,
    },
  };
};

export const useUpdateCustomDatasourceTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.customDatasource.update.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.customDatasource.customDatasources.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.customDatasource.customDatasourceById.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateCustomDatasource: async (
      variables: UpdateCustomDatasourceTRPCInput
    ): Promise<UpdateCustomDatasourceMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
